import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CampaignRow {
  campaign_id: string
  campaign_name: string
  advertiser_id: string
  account_name?: string | null
  status: string
  budget?: number | null
  bid?: number | null
  /** True when adgroups own budgets (non-CBO / ABO). */
  is_abo?: boolean
  cost: number
  revenue: number
  profit: number
  roi: number | null
  gclicks: number
  cpa: number
  rpc: number
  conversions: number
  clicks: number
  cpc: number
  cpm: number
  ctr: number | null
  cvr: number | null
  /** Roots g.pCTR — article → tile click */
  p1_cvr?: number | null
  /** Roots g.CTR — SERP → advertiser click */
  p2_cvr?: number | null
  impressions: number
  // ADW-specific
  visits?: number | null
  two_flow?: number | null
  rpm?: number | null
  one_to_two_flow?: number | null
  // Unassigned may carry extra fields
  [key: string]: unknown
}

export interface CampaignPagination {
  page: number
  page_size: number
  total: number
  pages: number
}

export interface CampaignTotals {
  cost: number
  revenue: number
  profit: number
  roi: number | null
  gclicks: number
  cpa: number
  rpc: number
  conversions: number
  clicks: number
  cpc: number
  cpm: number
  ctr: number | null
  cvr: number | null
  p1_cvr?: number | null
  p2_cvr?: number | null
  impressions: number
  visits?: number | null
  two_flow?: number | null
  rpm?: number | null
  one_to_two_flow?: number | null
  budget?: number | null
  bid?: number | null
  [key: string]: unknown
}

export interface CampaignsResponse {
  success: boolean
  data: CampaignRow[]
  pagination: CampaignPagination
  totals: CampaignTotals
}

export interface CampaignQueryParams {
  page?: number
  page_size?: number
  status?: string
  advertiser_ids?: string[]
  user_ids?: number[]
  search?: string
  start_date?: string
  end_date?: string
  sort_by?: string
  sort_order?: "asc" | "desc"
  network?: string
  /** JSON array of {id, min?, max?} for server-side metric range filters. */
  metric_filters?: string
}

export interface AdsManagerUser {
  id: number
  username: string
  role?: string | null
  is_active?: boolean
  label?: string
}

/** Row shape from `GET /api/analytics/keywords` (aggregated per keyword). */
export interface KeywordRow {
  keyword: string
  revenue?: number | null
  gclicks?: number | null
  rpc?: number | null
  second_flow_visits?: number | null
  monetization_ctr?: number | null
  record_count?: number | null
  [key: string]: unknown
}

export interface KeywordsResponse {
  success: boolean
  data: KeywordRow[]
}

export interface KeywordQueryParams {
  start_date: string
  end_date: string
  /** Ads Manager row `campaign_id` is the TikTok campaign id string, not internal DB id. */
  tiktok_campaign_id?: string
  offer_id?: string
  sort_by?: string
  sort_order?: "asc" | "desc"
  user_ids?: number[]
}

export interface AdGroupRow {
  adgroup_id: string
  adgroup_name: string
  campaign_id: string
  status?: string | null
  /** Adgroup settings (non-CBO); blank/0 when campaign uses CBO. */
  budget?: number | null
  bid?: number | null
  cost: number
  impressions: number
  clicks: number
  conversions: number
  cpc: number
  cpm: number
  ctr: number | null
  cpa: number
  cvr?: number | null
}

export interface AdGroupsResponse {
  success: boolean
  data: AdGroupRow[]
  message?: string
  campaign_id?: string
  start_date?: string
  end_date?: string
}

/** Live ad row from TikTok (not persisted). */
export interface LiveAdRow {
  ad_id: string
  ad_name: string
  status?: string | null
  ad_format?: string | null
  media_type: "video" | "image" | "carousel" | "unknown" | string
  thumbnail_url?: string | null
  /** Ordered still URLs for carousel / multi-image ads. */
  slide_urls?: string[]
  slide_count?: number
  preview_url?: string | null
  /** When "iframe", preview_url is a TikTok ads_preview HTML page. */
  preview_kind?: "iframe" | "media" | string | null
  video_id?: string | null
  image_ids?: string[]
  creative_count?: number
  is_smart_plus?: boolean
}

export interface LiveAdsResponse {
  success: boolean
  data: LiveAdRow[]
  message?: string
  adgroup_id?: string
  adgroup_name?: string
  advertiser_id?: string
  is_smart_plus?: boolean
}

export interface CampaignDailyRow {
  date: string
  cost: number
  revenue: number
  profit: number
  roi: number | null
  gclicks: number
  rpc: number
  clicks: number
  impressions: number
  conversions: number
  cpa: number
  cpc: number
  cpm: number
  ctr: number | null
  cvr: number | null
  p1_cvr?: number | null
  p2_cvr?: number | null
  budget?: number
  bid?: number
}

export interface CampaignDailyTotals {
  cost: number
  revenue: number
  profit: number
  roi: number | null
  gclicks: number
  rpc: number
  clicks: number
  impressions: number
  conversions: number
  cpa: number
  cpc: number
  cpm: number
  ctr: number | null
  cvr: number | null
  p1_cvr?: number | null
  p2_cvr?: number | null
}

export interface CampaignDailyResponse {
  success: boolean
  message?: string
  campaign?: {
    campaign_id: string
    campaign_name: string
    advertiser_id: string
    advertiser_name?: string | null
    status?: string | null
    objective_type?: string | null
    budget?: number
    bid?: number
  }
  data: CampaignDailyRow[]
  totals?: CampaignDailyTotals
  prior_totals?: CampaignDailyTotals
  prior_start_date?: string
  prior_end_date?: string
  best_day?: CampaignDailyRow | null
  worst_day?: CampaignDailyRow | null
  start_date?: string
  end_date?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Patch one campaign's status inside a cached list response (no refetch). */
export function patchCampaignStatusInResponse(
  response: CampaignsResponse | undefined,
  campaignId: string,
  status: string,
): CampaignsResponse | undefined {
  if (!response?.data?.length) return response
  let changed = false
  const data = response.data.map((row) => {
    if (row.campaign_id !== campaignId) return row
    changed = true
    return { ...row, status }
  })
  return changed ? { ...response, data } : response
}

/** Patch one adgroup's status inside a cached expand response (no refetch). */
export function patchAdgroupStatusInResponse(
  response: AdGroupsResponse | undefined,
  adgroupId: string,
  status: string,
): AdGroupsResponse | undefined {
  if (!response?.data?.length) return response
  let changed = false
  const data = response.data.map((row) => {
    if (row.adgroup_id !== adgroupId) return row
    changed = true
    return { ...row, status }
  })
  return changed ? { ...response, data } : response
}

function patchCampaignStatusInCache(
  qc: QueryClient,
  campaignId: string,
  status: string,
) {
  for (const kind of ["campaigns", "adw", "klow", "unassigned"] as const) {
    qc.setQueriesData<CampaignsResponse>(
      { queryKey: ["adsManager", kind] },
      (old) => patchCampaignStatusInResponse(old, campaignId, status),
    )
  }
}

function patchAdgroupStatusInCache(
  qc: QueryClient,
  adgroupId: string,
  status: string,
) {
  qc.setQueriesData<AdGroupsResponse>(
    { queryKey: ["adsManager", "adgroups"] },
    (old) => patchAdgroupStatusInResponse(old, adgroupId, status),
  )
}

function buildCampaignUrl(
  basePath: string,
  params: CampaignQueryParams,
): string {
  const sp = new URLSearchParams()
  if (params.page) sp.append("page", String(params.page))
  if (params.page_size) sp.append("page_size", String(params.page_size))
  if (params.status && params.status !== "all")
    sp.append("status", params.status)
  if (params.search) sp.append("search", params.search)
  if (params.start_date) sp.append("start_date", params.start_date)
  if (params.end_date) sp.append("end_date", params.end_date)
  if (params.sort_by) sp.append("sort_by", params.sort_by)
  if (params.sort_order) sp.append("sort_order", params.sort_order)
  if (params.network && params.network !== "all")
    sp.append("network", params.network)
  if (params.advertiser_ids?.length) {
    for (const id of params.advertiser_ids) sp.append("advertiser_ids", id)
  }
  if (params.user_ids?.length) {
    for (const id of params.user_ids) sp.append("user_ids", String(id))
  }
  if (params.metric_filters) {
    sp.append("metric_filters", params.metric_filters)
  }
  const qs = sp.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const ADS_MANAGER_QUERY_KEYS = {
  all: ["adsManager"] as const,
  campaigns: (p: CampaignQueryParams) =>
    ["adsManager", "campaigns", p] as const,
  adw: (p: CampaignQueryParams) => ["adsManager", "adw", p] as const,
  klow: (p: CampaignQueryParams) => ["adsManager", "klow", p] as const,
  unassigned: (p: CampaignQueryParams) =>
    ["adsManager", "unassigned", p] as const,
  users: (p: {
    startDate?: string
    endDate?: string
    alwaysIncludeUserId?: number
  }) => ["adsManager", "users", p] as const,
  keywords: (p: KeywordQueryParams) =>
    ["adsManager", "keywords", p] as const,
  adgroups: (campaignId: string, start: string, end: string) =>
    ["adsManager", "adgroups", campaignId, start, end] as const,
  adgroupAds: (adgroupId: string) =>
    ["adsManager", "adgroupAds", adgroupId] as const,
  campaignDaily: (campaignId: string, start: string, end: string) =>
    ["adsManager", "campaignDaily", campaignId, start, end] as const,
  campaignLandingUrl: (campaignId: string) =>
    ["adsManager", "campaignLandingUrl", campaignId] as const,
} as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAdsManagerCampaigns(
  params: CampaignQueryParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<CampaignsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.campaigns(params),
    queryFn: () =>
      apiFetch<CampaignsResponse>(
        buildCampaignUrl("/api/adsmanager/campaigns", params),
      ),
    enabled: (options.enabled ?? true) && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

export function useADWCampaigns(
  params: CampaignQueryParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<CampaignsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.adw(params),
    queryFn: () =>
      apiFetch<CampaignsResponse>(
        buildCampaignUrl("/api/adsmanager/adw/data", params),
      ),
    enabled: (options.enabled ?? true) && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

export function useKLOWCampaigns(
  params: CampaignQueryParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<CampaignsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.klow(params),
    queryFn: () =>
      apiFetch<CampaignsResponse>(
        buildCampaignUrl("/api/adsmanager/klow/data", params),
      ),
    enabled: (options.enabled ?? true) && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

export function useUnassignedCampaigns(
  params: CampaignQueryParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<CampaignsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.unassigned(params),
    queryFn: () =>
      apiFetch<CampaignsResponse>(
        buildCampaignUrl("/api/adsmanager/unassigned/data", params),
      ),
    enabled: (options.enabled ?? true) && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

export function useAdsManagerUsers(options?: {
  startDate?: string
  endDate?: string
  alwaysIncludeUserId?: number
}) {
  const startDate = options?.startDate
  const endDate = options?.endDate
  const alwaysIncludeUserId = options?.alwaysIncludeUserId
  const userQueryKey = {
    startDate,
    endDate,
    alwaysIncludeUserId,
  }

  return useQuery<AdsManagerUser[]>({
    queryKey: ADS_MANAGER_QUERY_KEYS.users(userQueryKey),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (startDate && endDate) {
        params.set("start_date", startDate)
        params.set("end_date", endDate)
      }
      if (alwaysIncludeUserId != null) {
        params.set("always_include_user_id", String(alwaysIncludeUserId))
      }
      const qs = params.toString()
      const res = await apiFetch<{ success: boolean; users: AdsManagerUser[] }>(
        qs ? `/api/adsmanager/users?${qs}` : "/api/adsmanager/users",
      )
      return res.users ?? []
    },
    enabled: !startDate || Boolean(startDate && endDate),
    retry: false,
  })
}

type StatusMutationResult = {
  success: boolean
  status?: string
  campaign_id?: string
  adgroup_id?: string
  message?: string
}

export function usePauseCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (campaignId: string) =>
      apiFetch<StatusMutationResult>(
        `/api/adsmanager/campaign/${campaignId}/pause`,
        { method: "POST" },
      ),
    onSuccess: (data, campaignId) => {
      if (!data?.success) return
      // Patch only this campaign row — never invalidate live ad expands.
      patchCampaignStatusInCache(
        qc,
        campaignId,
        data.status ?? "DISABLE",
      )
    },
  })
}

export function useResumeCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (campaignId: string) =>
      apiFetch<StatusMutationResult>(
        `/api/adsmanager/campaign/${campaignId}/resume`,
        { method: "POST" },
      ),
    onSuccess: (data, campaignId) => {
      if (!data?.success) return
      patchCampaignStatusInCache(qc, campaignId, data.status ?? "ENABLE")
    },
  })
}

export function usePauseAdgroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (adgroupId: string) =>
      apiFetch<StatusMutationResult>(
        `/api/adsmanager/adgroup/${encodeURIComponent(adgroupId)}/pause`,
        { method: "POST" },
      ),
    onSuccess: (data, adgroupId) => {
      if (!data?.success) return
      patchAdgroupStatusInCache(qc, adgroupId, data.status ?? "DISABLE")
    },
  })
}

export function useResumeAdgroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (adgroupId: string) =>
      apiFetch<StatusMutationResult>(
        `/api/adsmanager/adgroup/${encodeURIComponent(adgroupId)}/resume`,
        { method: "POST" },
      ),
    onSuccess: (data, adgroupId) => {
      if (!data?.success) return
      patchAdgroupStatusInCache(qc, adgroupId, data.status ?? "ENABLE")
    },
  })
}

type BudgetBidMutationResult = {
  success: boolean
  adgroup_id?: string
  budget?: number | null
  bid?: number | null
  message?: string
}

function patchAdgroupBudgetBidInCache(
  qc: QueryClient,
  adgroupId: string,
  patch: { budget?: number | null; bid?: number | null },
) {
  qc.setQueriesData<AdGroupsResponse>(
    { queryKey: ["adsManager", "adgroups"] },
    (old) => {
      if (!old?.data?.length) return old
      let changed = false
      const data = old.data.map((ag) => {
        if (ag.adgroup_id !== adgroupId) return ag
        changed = true
        return {
          ...ag,
          ...(patch.budget !== undefined ? { budget: patch.budget } : {}),
          ...(patch.bid !== undefined ? { bid: patch.bid } : {}),
        }
      })
      return changed ? { ...old, data } : old
    },
  )
}

export function useUpdateAdgroupBudgetBid() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: {
      adgroupId: string
      budget?: number | null
      bid?: number | null
    }) =>
      apiFetch<BudgetBidMutationResult>(
        `/api/adsmanager/adgroup/${encodeURIComponent(args.adgroupId)}/budget-bid`,
        {
          method: "POST",
          body: {
            budget: args.budget ?? undefined,
            bid: args.bid ?? undefined,
          },
        },
      ),
    onSuccess: (data, vars) => {
      if (!data?.success) return
      patchAdgroupBudgetBidInCache(qc, vars.adgroupId, {
        budget: data.budget ?? vars.budget,
        bid: data.bid ?? vars.bid,
      })
    },
  })
}

export interface AdgroupDuplicatePreview {
  adgroup_id: string
  campaign_id: string
  campaign_name: string | null
  advertiser_id: string | null
  objective_type: string | null
  adgroup_name: string
  suggested_name: string
  budget: number | null
  budget_mode: string | null
  is_cbo: boolean
  is_smart_plus?: boolean
  conversion_bid_price: number | null
  bid_type: string | null
  location_ids: string[]
  schedule_type: string | null
  schedule_start_time: string | null
}

export interface DuplicateAdgroupPayload {
  adgroup_name?: string | null
  budget?: number | null
  conversion_bid_price?: number | null
  location_ids?: string[] | null
  start_paused?: boolean
  schedule_start_now?: boolean
}

export function useAdgroupDuplicatePreview(
  adgroupId: string | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery<{ success: boolean; data: AdgroupDuplicatePreview }>({
    queryKey: ["adsManager", "adgroupDuplicatePreview", adgroupId] as const,
    queryFn: () =>
      apiFetch<{ success: boolean; data: AdgroupDuplicatePreview }>(
        `/api/campaigns/adgroups/${encodeURIComponent(adgroupId!)}/duplicate-preview`,
      ),
    enabled: (options.enabled ?? true) && Boolean(adgroupId),
    retry: false,
  })
}

export function useDuplicateAdgroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      adgroupId,
      body,
    }: {
      adgroupId: string
      body: DuplicateAdgroupPayload
    }) =>
      apiFetch<{
        success: boolean
        adgroup_id: string
        adgroup_name: string
        status: string
        message?: string
      }>(`/api/campaigns/adgroups/${encodeURIComponent(adgroupId)}/duplicate`, {
        method: "POST",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADS_MANAGER_QUERY_KEYS.all })
    },
  })
}

export function useKeywordAnalytics(
  params: KeywordQueryParams,
  options: { enabled?: boolean } = {},
) {
  const sp = new URLSearchParams()
  sp.append("start_date", params.start_date)
  sp.append("end_date", params.end_date)
  if (params.tiktok_campaign_id)
    sp.append("tiktok_campaign_id", params.tiktok_campaign_id)
  if (params.offer_id) sp.append("offer_id", params.offer_id)
  if (params.sort_by) sp.append("sort_by", params.sort_by)
  if (params.sort_order) sp.append("sort_order", params.sort_order)
  if (params.user_ids?.length) {
    for (const userId of params.user_ids) {
      sp.append("user_ids", String(userId))
    }
  }

  return useQuery<KeywordsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.keywords(params),
    queryFn: () =>
      apiFetch<KeywordsResponse>(
        `/api/analytics/keywords?${sp.toString()}`,
      ),
    enabled: (options.enabled ?? true) && Boolean(params.start_date && params.end_date),
    retry: false,
  })
}

function patchCampaignAboInCache(qc: QueryClient, campaignId: string) {
  for (const kind of ["campaigns", "adw", "klow", "unassigned"] as const) {
    qc.setQueriesData<CampaignsResponse>(
      { queryKey: ["adsManager", kind] },
      (old) => {
        if (!old?.data?.length) return old
        let changed = false
        const data = old.data.map((row) => {
          if (row.campaign_id !== campaignId || row.is_abo) return row
          changed = true
          return { ...row, is_abo: true, bid: null }
        })
        return changed ? { ...old, data } : old
      },
    )
  }
}

export function useCampaignAdgroups(
  campaignId: string | null,
  startDate: string,
  endDate: string,
  options: { enabled?: boolean } = {},
) {
  const qc = useQueryClient()
  const enabled =
    (options.enabled ?? true) &&
    Boolean(campaignId && startDate && endDate)

  return useQuery<AdGroupsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.adgroups(
      campaignId ?? "",
      startDate,
      endDate,
    ),
    queryFn: async () => {
      const sp = new URLSearchParams()
      sp.set("start_date", startDate)
      sp.set("end_date", endDate)
      const res = await apiFetch<AdGroupsResponse>(
        `/api/adsmanager/campaigns/${encodeURIComponent(campaignId!)}/adgroups?${sp}`,
      )
      // Live enrich often finds adgroup budgets before list is_abo is set.
      if (
        campaignId &&
        res?.data?.some((ag) => Number(ag.budget) > 0)
      ) {
        patchCampaignAboInCache(qc, campaignId)
      }
      return res
    },
    enabled,
    retry: false,
  })
}

/** Live ads under an adgroup — TikTok on demand, not stored in DB. */
export function useAdgroupAds(
  adgroupId: string | null,
  options: { enabled?: boolean } = {},
) {
  const enabled = (options.enabled ?? true) && Boolean(adgroupId)

  return useQuery<LiveAdsResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.adgroupAds(adgroupId ?? ""),
    queryFn: () =>
      apiFetch<LiveAdsResponse>(
        `/api/adsmanager/adgroup/${encodeURIComponent(adgroupId!)}/ads`,
      ),
    enabled,
    retry: false,
    staleTime: 60_000,
  })
}

export interface CampaignLandingUrlResponse {
  success: boolean
  landing_page_url?: string | null
  message?: string
  campaign_id?: string
  source?: string
  adgroup_id?: string
}

/** Live landing page URL from TikTok ads under the campaign. */
export function useCampaignLandingUrl(
  campaignId: string | null,
  options: { enabled?: boolean } = {},
) {
  const enabled = (options.enabled ?? true) && Boolean(campaignId)
  return useQuery<CampaignLandingUrlResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.campaignLandingUrl(campaignId ?? ""),
    queryFn: () =>
      apiFetch<CampaignLandingUrlResponse>(
        `/api/adsmanager/campaigns/${encodeURIComponent(campaignId!)}/landing-url`,
      ),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  })
}

/** Per-day DailyMetric series for one campaign (calendar-filled). */
export function useCampaignDailyMetrics(
  campaignId: string | null,
  startDate: string,
  endDate: string,
  options: { enabled?: boolean } = {},
) {
  const enabled =
    (options.enabled ?? true) &&
    Boolean(campaignId && startDate && endDate)

  return useQuery<CampaignDailyResponse>({
    queryKey: ADS_MANAGER_QUERY_KEYS.campaignDaily(
      campaignId ?? "",
      startDate,
      endDate,
    ),
    queryFn: () => {
      const sp = new URLSearchParams()
      sp.set("start_date", startDate)
      sp.set("end_date", endDate)
      return apiFetch<CampaignDailyResponse>(
        `/api/adsmanager/campaigns/${encodeURIComponent(campaignId!)}/daily?${sp}`,
      )
    },
    enabled,
    retry: false,
  })
}
